import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchWithRetry } from '../../util/sliceUtils';

const backendUrl = process.env.REACT_APP_BackendUrl;
const TRAFFICAMOUNT_URL = backendUrl + '/v1/trafficAmount';
const CONGESTION_URL = backendUrl + '/v1/congestion';
const QUEUELENGTH_URL = backendUrl + '/v1/queueLength';
const DEVICESTATE_URL = backendUrl + '/v1/deviceState';
const DEVICE_PREFIX = 'tre';

export const deviceStateSlice = createSlice({
  name: 'deviceState',
  initialState: {
    trafficAmount: {},
    congestion: {},
    queueLength: {},
    deviceState: {},
    trafficAmountLoading: false,
    congestionLoading: false,
    queueLengthLoading: false,
    deviceStateLoading: false,
    deviceStateError: null,
    deviceStateRetryCount: 0,
  },
  reducers: {
    setTrafficAmount: (state, action) => {
      state.trafficAmount = action.payload;
    },
    setCongestion: (state, action) => {
      state.congestion = action.payload;
    },
    setQueueLength: (state, action) => {
      state.queueLength = action.payload;
    },
    setDeviceState: (state, action) => {
      state.deviceState = action.payload;
      state.deviceStateError = null;
      state.deviceStateRetryCount = 0;
    },
    setTrafficAmountLoading: (state, action) => {
      state.trafficAmountLoading = action.payload;
    },
    setCongestionLoading: (state, action) => {
      state.congestionLoading = action.payload;
    },
    setQueueLengthLoading: (state, action) => {
      state.queueLengthLoading = action.payload;
    },
    setDeviceStateLoading: (state, action) => {
      state.deviceStateLoading = action.payload;
    },
    setDeviceStateError: (state, action) => {
      state.deviceStateError = action.payload;
    },
    incrementDeviceStateRetryCount: (state) => {
      state.deviceStateRetryCount += 1;
    },
    resetDeviceStateRetryCount: (state) => {
      state.deviceStateRetryCount = 0;
    },
  },
});

export const {
  setTrafficAmount, setCongestion, setQueueLength, setDeviceState,
  setTrafficAmountLoading, setCongestionLoading, setQueueLengthLoading, setDeviceStateLoading,
  setDeviceStateError, incrementDeviceStateRetryCount, resetDeviceStateRetryCount
} = deviceStateSlice.actions;

// Store last device name for retry
let lastDeviceName = null;

// Retry thunk for manual retry from toast - this is what gets dispatched when clicking retry
export const retryDeviceState = () => (dispatch) => {
  console.log('Manually retrying device state...');
  dispatch(resetDeviceStateRetryCount());
  if (lastDeviceName) {
    dispatch(getDeviceState(lastDeviceName));
  }
};

export const getTrafficAmount = (deviceName, detectorName, startTime, endTime) => dispatch => {
  const pathParameters = getPathParameters(deviceName, detectorName);
  const queryParameters = getQueryParameters(startTime, endTime);
  const config = {
    headers: {}
  };
  dispatch(setTrafficAmountLoading(true));
  const url = `${TRAFFICAMOUNT_URL}${pathParameters}${queryParameters}`;
  axios.get(url, config).then(
    response => {
      dispatch(setTrafficAmount(response.data));
      dispatch(setTrafficAmountLoading(false));
    },
    error => {
      console.log("Intersections error", error);
      dispatch(setTrafficAmountLoading(false));
    }
  );
};

export const getCongestion = (deviceName, detectorName, startTime, endTime) => dispatch => {
  const pathParameters = getPathParameters(deviceName, detectorName);
  const queryParameters = getQueryParameters(startTime, endTime);
  const config = {
    headers: {}
  };
  dispatch(setCongestionLoading(true));
  const url = `${CONGESTION_URL}${pathParameters}${queryParameters}`;
  axios.get(url, config).then(
    response => {
      dispatch(setCongestion(response.data));
      dispatch(setCongestionLoading(false));
    },
    error => {
      console.log("Congestion error", error);
      dispatch(setCongestionLoading(false));
    }
  );
};

export const getQueueLength = (deviceName, detectorName, startTime, endTime) => dispatch => {
  const pathParameters = getPathParameters(deviceName, detectorName);
  const queryParameters = getQueryParameters(startTime, endTime);
  const config = {
    headers: {}
  };
  dispatch(setQueueLengthLoading(true));
  const url = `${QUEUELENGTH_URL}${pathParameters}${queryParameters}`;
  axios.get(url, config).then(
    response => {
      dispatch(setQueueLength(response.data));
      dispatch(setQueueLengthLoading(false));
    },
    error => {
      console.log("QueueLength error", error);
      dispatch(setQueueLengthLoading(false));
    }
  );
};

export const getDeviceState = (deviceName, startTime, endTime, forceRetry = false) => {
  // Store device name for potential manual retry
  lastDeviceName = deviceName;

  const pathParameters = getPathParameters(deviceName, null);
  const queryParameters = getQueryParameters(startTime, endTime);
  const url = `${DEVICESTATE_URL}${pathParameters}${queryParameters}`;

  return fetchWithRetry({
    fetchFn: () => axios.get(url, { headers: {} }),
    actions: {
      setData: setDeviceState,
      setLoading: setDeviceStateLoading,
      setError: setDeviceStateError,
      incrementRetry: incrementDeviceStateRetryCount,
    },
    selectors: {
      selectRetryCount: selectDeviceStateRetryCount,
      selectLoading: selectDeviceStateLoading,
    },
    retryActionName: 'RETRY_DEVICE_STATE',
    errorMessagePrefix: `Cannot load device state for ${DEVICE_PREFIX}${deviceName}`,
    retryThunk: (force) => getDeviceState(deviceName, startTime, endTime, force),
    shouldRetry: (state) => {
      const { isDrivingMode, isHudVisible, activeLight } = state.map;
      if (!isDrivingMode || !isHudVisible) return false;
      if (activeLight && activeLight.liva_nro !== deviceName) return false;
      return true;
    }
  }, forceRetry);
};


export const selectTrafficAmount = state => state.deviceState.trafficAmount;
export const selectCongestion = state => state.deviceState.congestion;
export const selectQueueLength = state => state.deviceState.queueLength;
export const selectDeviceState = state => state.deviceState.deviceState;

export const selectTrafficAmountLoading = state => state.deviceState.trafficAmountLoading;
export const selectCongestionLoading = state => state.deviceState.congestionLoading;
export const selectQueueLengthLoading = state => state.deviceState.queueLengthLoading;
export const selectDeviceStateLoading = state => state.deviceState.deviceStateLoading;
export const selectDeviceStateError = state => state.deviceState.deviceStateError;
export const selectDeviceStateRetryCount = state => state.deviceState.deviceStateRetryCount;

export default deviceStateSlice.reducer;

const getPathParameters = (deviceName, detectorName) => {
  return deviceName && detectorName ? `/${DEVICE_PREFIX}${deviceName}/${detectorName}` :
    deviceName ? `/${DEVICE_PREFIX}${deviceName}` : '';
};


const getQueryParameters = (startTime, endTime) => {
  if ((startTime || endTime) && !(startTime && endTime)) {
    console.error('Both startTime and endTime are needed if either is defined.');
    return '';
  }
  return startTime && endTime ? `?startTime=${startTime}&endTime=${endTime}` : '';
};
